import React, { useId } from 'react';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import FormLabel from '@mui/material/FormLabel';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { Theme } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import TiptapToolbar from './Toolbar';
import messages from './messages';
import Attachments, { Attachment } from './Attachments';
import { TiptapEditor, useTiptap } from './Tiptap';

type TiptapProps = {
  onChange: (value: string) => void;
  value: string;
  errorMessage?: string;
  attachments: Attachment[];
  append: (attachment: Attachment) => void;
  remove: (index: number) => void;
};

const Tiptap = ({
  ref,
  ...props
}: TiptapProps & { ref?: React.Ref<HTMLFieldSetElement> }) => {
  const labelId = useId();
  const editor = useTiptap({
    content: props.value,
    onUpdate({ editor: updatedEditor }) {
      props.onChange(updatedEditor.getText() ? updatedEditor.getHTML() : '');
    },
    editorProps: {
      attributes: {
        role: 'textbox',
        'aria-labelledby': labelId,
      },
    },
  });

  return (
    <>
      <FormControl
        sx={{ m: 3 }}
        component="fieldset"
        variant="outlined"
        ref={ref}
      >
        <FormLabel component="legend" id={labelId}>
          <FormattedMessage {...messages.content} />
        </FormLabel>
        <TiptapToolbar editor={editor} />
        <TiptapEditor
          styles={{
            outlineOffset: '-1px',
            outline: '1px solid',
            outlineColor: (theme: Theme) =>
              props.errorMessage
                ? theme.palette.error.main
                : theme.palette.grey[400],
            borderRadius: (theme: Theme) =>
              `0 0 ${theme.shape.borderRadius}px ${theme.shape.borderRadius}px`,
            boxSizing: 'content-box',
            ':hover': {
              outlineColor: (theme: Theme) =>
                props.errorMessage
                  ? theme.palette.error.main
                  : theme.palette.grey[800],
            },
            ':focus-within': {
              outline: '2px solid',
              outlineColor: (theme: Theme) =>
                props.errorMessage
                  ? theme.palette.error.main
                  : theme.palette.primary.main,
            },
            '[contenteditable=true]': {
              p: 2,
              outline: 'none',
            },
            'img.ProseMirror-selectednode': {
              outline: '2px solid',
              outlineColor: (theme: Theme) => theme.palette.primary.main,
            },
          }}
          editor={editor}
        />
        <FormHelperText error={Boolean(props.errorMessage)}>
          {props.errorMessage}
        </FormHelperText>
      </FormControl>
      <Stack>
        <Typography color="textSecondary" variant="caption">
          <FormattedMessage {...messages.attachments} />
        </Typography>
        <Attachments
          fields={props.attachments}
          append={(attachment) => {
            props.append(attachment);
            if (attachment.contentType?.includes('image')) {
              editor?.commands.setImage({
                src: `/api/Blobs/${attachment.blobId}`,
              });
            }
          }}
          remove={props.remove}
        />
      </Stack>
    </>
  );
};

export default Tiptap;
